import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import {
    configuratorPromoLineComponentsMap,
    footerComponentsMap,
} from "../blocks/componentMaps";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductDisplayPageBody as ProductDisplayPageBodyContent } from "../components/ProductPageBody";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as ProductHeroAssembly } from "./ProductHeroAssemblyTemplate";

const ProductDisplayPageFinePrint = (
    props: Immutable<{ page: templates.ProductDisplayPageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.fine_print) {
        return null;
    }
    return (
        <>
            {props.page.fine_print.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={footerComponentsMap}
                />
            ))}
        </>
    );
};

const ProductDisplayPageConfiguratorPromoLine = (
    props: Immutable<{ page: templates.ProductDisplayPageTemplate["page"] }>,
): React.JSX.Element => (
    <>
        {props.page.configurator_promo_line.value.map((block) => (
            <StreamField
                key={block.id}
                tagName="div"
                attrs={{
                    className: `w-block-${block.type} block-${block.type}`,
                }}
                value={[block]}
                components={configuratorPromoLineComponentsMap}
            />
        ))}
    </>
);

const ProductDisplayPageBody = (
    props: Immutable<{
        page: templates.ProductDisplayPageTemplate["page"];
        reviews: templates.ProductDisplayPageTemplate["reviews"];
        change_root_ssr: templates.ProductDisplayPageTemplate["change_root_ssr"];
        root_conditional_ssr: templates.ProductDisplayPageTemplate["root_conditional_ssr"];
    }>,
): React.JSX.Element | null => {
    if (!props.page.body) {
        return null;
    }
    return (
        <ProductDisplayPageBodyContent
            value={props.page.body.value.filter(
                (block) => block.type !== "gallery_block",
            )}
            reviews={props.reviews}
            changeRootSSR={props.change_root_ssr}
            rootConditionalSSR={props.root_conditional_ssr}
            pageSlug={props.page.slug}
        />
    );
};

export const Template = (
    props: Immutable<templates.ProductDisplayPageTemplate>,
): React.JSX.Element => {
    const { page } = props;
    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            afterFooter={<ProductDisplayPageFinePrint page={page} />}
        >
            <section className="product-hero">
                <div className="product-hero__blocks">
                    <ProductHeroAssembly {...props.hero} />
                </div>
                <div
                    className={`product-hero__configurator ${page.configurator_background_style || ""}`}
                >
                    <div className="product-hero__configurator__promo-line">
                        <ProductDisplayPageConfiguratorPromoLine page={page} />
                    </div>
                    <ModularConfigurator {...props.configurator} />
                </div>
            </section>

            <section className={`pdp-blocks pdp-blocks--${page.slug}`}>
                <ProductDisplayPageBody
                    page={page}
                    reviews={props.reviews}
                    change_root_ssr={props.change_root_ssr}
                    root_conditional_ssr={props.root_conditional_ssr}
                />
            </section>
        </PageSkeleton>
    );
};
